/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputAdornment,
  Grid,
  Slider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  DateRange as DateRangeIcon,
  Speed as SpeedIcon,
  Person as PersonIcon,
  Description as FileIcon,
  Error as ErrorIcon,
  Token as TokenIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { subHours, subDays } from 'date-fns';

// All possible operations grouped by subsystem (based on real data analysis)
const OPERATIONS_BY_SUBSYSTEM = {
  ingestion: {
    name: 'Ingestion',
    operations: ['file_download', 'gcs_upload', 'ingestion_complete']
  },
  ptl: {
    name: 'Privacy Tokenization',
    operations: [
      'kms_encrypt', 'kms_decrypt', 'tokenization', 'tokenization_complete',
      'bigquery_storage', 'batch_processing', 'api_process_request'
    ]
  },
  te: {
    name: 'Token Examiner',
    operations: [
      'checksum_calculate', 'checksum_decision', 'entity_extraction',
      'token_discovery', 'similarity_match', 'entity_recording',
      'cache_hit', 'cache_miss', 'bigquery_storage', 'processing_complete'
    ]
  }
};

const COMMON_MIME_TYPES = [
  { value: 'text/plain', label: 'Plain Text (.txt)' },
  { value: 'text/csv', label: 'CSV (.csv)' },
  { value: 'text/html', label: 'HTML (.html)' },
  { value: 'text/markdown', label: 'Markdown (.md)' },
  { value: 'application/pdf', label: 'PDF (.pdf)' },
  { value: 'application/json', label: 'JSON (.json)' },
  { value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'Word (.docx)' },
  { value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel (.xlsx)' },
  { value: 'application/vnd.google-apps.document', label: 'Google Docs' },
  { value: 'application/vnd.google-apps.spreadsheet', label: 'Google Sheets' }
];

const COMMON_FILE_EXTENSIONS = [
  { value: '.txt', label: 'Text Files (.txt)' },
  { value: '.pdf', label: 'PDF Files (.pdf)' },
  { value: '.docx', label: 'Word Documents (.docx)' },
  { value: '.xlsx', label: 'Excel Spreadsheets (.xlsx)' },
  { value: '.csv', label: 'CSV Files (.csv)' },
  { value: '.json', label: 'JSON Files (.json)' },
  { value: '.html', label: 'HTML Files (.html)' },
  { value: '.md', label: 'Markdown Files (.md)' }
];

const ARTIFACT_TYPES = [
  { value: 'file', label: 'File Processing', description: 'Documents with filename/MIME type metadata' },
  { value: 'token', label: 'Token Processing', description: 'Entity extraction and token operations' },
  { value: 'bot', label: 'System/API Processing', description: 'Automated system and API operations' },
  { value: 'unknown', label: 'Unknown Type', description: 'Operations without clear classification' }
];

const TIME_PRESETS = [
  { label: 'Last Hour', getValue: () => subHours(new Date(), 1) },
  { label: 'Last 6 Hours', getValue: () => subHours(new Date(), 6) },
  { label: 'Last 24 Hours', getValue: () => subDays(new Date(), 1) },
  { label: 'Last 3 Days', getValue: () => subDays(new Date(), 3) },
  { label: 'Last Week', getValue: () => subDays(new Date(), 7) },
  { label: 'Last Month', getValue: () => subDays(new Date(), 30) }
];

 
 
 
 
 
 
 
 
// eslint-disable-next-line max-lines-per-function
function ProcessVisibilityAdvancedFilters({ onFiltersChange, onSearch }) {
  // Core filters state
  const [searchTerm, setSearchTerm] = useState('');
  const [subsystems, setSubsystems] = useState([]);
  const [operations, setOperations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [levels, setLevels] = useState([]);

  // Date/time filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Document filters
  const [fileName, setFileName] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [mimeTypes, setMimeTypes] = useState([]);
  const [fileExtensions, setFileExtensions] = useState([]);
  const [artifactTypes, setArtifactTypes] = useState([]);
  const [fileSizeRange, setFileSizeRange] = useState([0, 100]);

  // Processing filters
  const [hasErrors, setHasErrors] = useState(false);
  const [hasTokens, setHasTokens] = useState(false);
  const [durationRange, setDurationRange] = useState([0, 100]);

  // User filter
  const [userId, setUserId] = useState('');

  // UI state
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    datetime: false,
    document: false,
    processing: false,
    advanced: false
  });

  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Calculate active filters count
  useEffect(() => {
    let count = 0;
    if (searchTerm) count++;
    if (subsystems.length > 0) count++;
    if (operations.length > 0) count++;
    if (statuses.length > 0) count++;
    if (levels.length > 0) count++;
    if (startDate || endDate) count++;
    if (fileName) count++;
    if (documentId) count++;
    if (mimeTypes.length > 0) count++;
    if (fileExtensions.length > 0) count++;
    if (artifactTypes.length > 0) count++;
    if (fileSizeRange[0] > 0 || fileSizeRange[1] < 100) count++;
    if (hasErrors) count++;
    if (hasTokens) count++;
    if (durationRange[0] > 0 || durationRange[1] < 100) count++;
    if (userId) count++;

    setActiveFiltersCount(count);
  }, [searchTerm, subsystems, operations, statuses, levels, startDate, endDate,
      fileName, documentId, mimeTypes, fileExtensions, artifactTypes, fileSizeRange, hasErrors, hasTokens,
      durationRange, userId]);

  // Build filters object
  const buildFilters = () => {
    const filters = {};

    if (searchTerm) filters.searchTerm = searchTerm;
    if (subsystems.length > 0) filters.subsystems = subsystems;
    if (operations.length > 0) filters.operations = operations;
    if (statuses.length > 0) filters.statuses = statuses;
    if (levels.length > 0) filters.levels = levels;
    if (startDate) filters.startDate = startDate.toISOString();
    if (endDate) filters.endDate = endDate.toISOString();
    if (fileName) filters.fileName = fileName;
    if (documentId) filters.documentId = documentId;
    if (mimeTypes.length > 0) filters.mimeTypes = mimeTypes;
    if (fileExtensions.length > 0) filters.fileExtensions = fileExtensions;
    if (artifactTypes.length > 0) filters.artifactTypes = artifactTypes;

    // Convert slider ranges to actual values
    if (fileSizeRange[0] > 0 || fileSizeRange[1] < 100) {
      filters.minFileSize = Math.pow(10, fileSizeRange[0] / 10); // Exponential scale
      filters.maxFileSize = Math.pow(10, fileSizeRange[1] / 10);
    }

    if (hasErrors) filters.hasErrors = true;
    if (hasTokens) filters.hasTokens = true;

    if (durationRange[0] > 0 || durationRange[1] < 100) {
      filters.minDuration = durationRange[0] * 10; // 0-1000ms scale
      filters.maxDuration = durationRange[1] * 10;
    }

    if (userId) filters.userId = userId;

    return filters;
  };

  // Handle search
  const handleSearch = () => {
    const filters = buildFilters();
    console.log('[Advanced Filters] Search clicked with filters:', filters);
    onSearch(filters);
  };

  // Handle filter changes
  useEffect(() => {
    const filters = buildFilters();
    console.log('[Advanced Filters] Filters changed:', {
      searchTerm,
      fileExtensions,
      artifactTypes,
      allFilters: filters
    });
    onFiltersChange(filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, subsystems, operations, statuses, levels, startDate, endDate,
      fileName, documentId, mimeTypes, fileExtensions, artifactTypes, fileSizeRange, hasErrors, hasTokens,
      durationRange, userId]);

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setSubsystems([]);
    setOperations([]);
    setStatuses([]);
    setLevels([]);
    setStartDate(null);
    setEndDate(null);
    setFileName('');
    setDocumentId('');
    setMimeTypes([]);
    setFileExtensions([]);
    setArtifactTypes([]);
    setFileSizeRange([0, 100]);
    setHasErrors(false);
    setHasTokens(false);
    setDurationRange([0, 100]);
    setUserId('');
  };

  // Format file size for display
  const formatFileSize = (value) => {
    const bytes = Math.pow(10, value / 10);
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={2} sx={{ p: 3, mb: 3, border: '2px solid #1976d2' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Advanced Filters (NEW)
            {activeFiltersCount > 0 && (
              <Chip
                size="small"
                label={`${activeFiltersCount} active`}
                color="primary"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={clearAllFilters}
            disabled={activeFiltersCount === 0}
          >
            Clear All
          </Button>
        </Box>

        {/* Basic Search */}
        <Accordion expanded={expandedSections.basic} onChange={() => toggleSection('basic')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Basic Filters</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Search"
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    console.log('[Advanced Filters] Search term changed to:', value);
                    setSearchTerm(value);
                  }}
                  placeholder="Search in file names, document IDs, messages, tokens..."
                  helperText={searchTerm ? `Searching for: "${searchTerm}"` : 'Enter search term and click Search button'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <FormControl fullWidth>
                  <InputLabel>Subsystems</InputLabel>
                  <Select
                    multiple
                    value={subsystems}
                    onChange={(e) => setSubsystems(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {['ingestion', 'ptl', 'te'].map((subsystem) => (
                      <MenuItem key={subsystem} value={subsystem}>
                        <Checkbox checked={subsystems.indexOf(subsystem) > -1} />
                        <ListItemText primary={OPERATIONS_BY_SUBSYSTEM[subsystem]?.name || subsystem} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    multiple
                    value={statuses}
                    onChange={(e) => setStatuses(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {['started', 'progress', 'success', 'error'].map((status) => (
                      <MenuItem key={status} value={status}>
                        <Checkbox checked={statuses.indexOf(status) > -1} />
                        <ListItemText primary={status} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Operations</InputLabel>
                  <Select
                    multiple
                    value={operations}
                    onChange={(e) => setOperations(e.target.value)}
                    renderValue={(selected) => `${selected.length} selected`}
                  >
                    {Object.entries(OPERATIONS_BY_SUBSYSTEM).map(([subsystem, config]) => [
                      <MenuItem key={`${subsystem}-header`} disabled>
                        <Typography variant="subtitle2" color="primary">
                          {config.name}
                        </Typography>
                      </MenuItem>,
                      ...config.operations.map((op) => (
                        <MenuItem key={op} value={op} sx={{ pl: 4 }}>
                          <Checkbox checked={operations.indexOf(op) > -1} />
                          <ListItemText primary={op} />
                        </MenuItem>
                      ))
                    ]).flat()}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Date/Time Filters */}
        <Accordion expanded={expandedSections.datetime} onChange={() => toggleSection('datetime')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              <DateRangeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Date & Time
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {TIME_PRESETS.map((preset) => (
                    <Button
                      key={preset.label}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setStartDate(preset.getValue());
                        setEndDate(new Date());
                      }}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </Box>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <DateTimePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <DateTimePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Document Filters */}
        <Accordion expanded={expandedSections.document} onChange={() => toggleSection('document')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              <FileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Document Properties
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="File Name"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  placeholder="Contains text..."
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="Document ID"
                  value={documentId}
                  onChange={(e) => setDocumentId(e.target.value)}
                  placeholder="Exact match"
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <FormControl fullWidth>
                  <InputLabel>MIME Types</InputLabel>
                  <Select
                    multiple
                    value={mimeTypes}
                    onChange={(e) => setMimeTypes(e.target.value)}
                    renderValue={(selected) => `${selected.length} selected`}
                  >
                    {COMMON_MIME_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Checkbox checked={mimeTypes.indexOf(type.value) > -1} />
                        <ListItemText primary={type.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <FormControl fullWidth>
                  <InputLabel>File Extensions</InputLabel>
                  <Select
                    multiple
                    value={fileExtensions}
                    onChange={(e) => setFileExtensions(e.target.value)}
                    renderValue={(selected) => `${selected.length} selected`}
                  >
                    {COMMON_FILE_EXTENSIONS.map((ext) => (
                      <MenuItem key={ext.value} value={ext.value}>
                        <Checkbox checked={fileExtensions.indexOf(ext.value) > -1} />
                        <ListItemText primary={ext.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Artifact Type</InputLabel>
                  <Select
                    multiple
                    value={artifactTypes}
                    onChange={(e) => setArtifactTypes(e.target.value)}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const type = ARTIFACT_TYPES.find(t => t.value === value);
                          return (
                            <Chip key={value} label={type?.label || value} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {ARTIFACT_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Checkbox checked={artifactTypes.indexOf(type.value) > -1} />
                        <ListItemText
                          primary={type.label}
                          secondary={type.description}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <Typography variant="caption" gutterBottom>
                  File Size Range
                </Typography>
                <Slider
                  value={fileSizeRange}
                  onChange={(e, newValue) => setFileSizeRange(newValue)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={formatFileSize}
                  min={0}
                  max={100}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 30, label: '1KB' },
                    { value: 60, label: '1MB' },
                    { value: 90, label: '1GB' }
                  ]}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Processing Filters */}
        <Accordion expanded={expandedSections.processing} onChange={() => toggleSection('processing')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Processing Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasErrors}
                      onChange={(e) => setHasErrors(e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ErrorIcon color="error" />
                      Errors Only
                    </Box>
                  }
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasTokens}
                      onChange={(e) => setHasTokens(e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TokenIcon />
                      Has Tokens
                    </Box>
                  }
                />
              </Grid>

              <Grid size={12}>
                <Typography variant="caption" gutterBottom>
                  Processing Duration (ms)
                </Typography>
                <Slider
                  value={durationRange}
                  onChange={(e, newValue) => setDurationRange(newValue)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value * 10}ms`}
                  min={0}
                  max={100}
                  marks={[
                    { value: 0, label: '0ms' },
                    { value: 25, label: '250ms' },
                    { value: 50, label: '500ms' },
                    { value: 75, label: '750ms' },
                    { value: 100, label: '1s+' }
                  ]}
                />
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Log Levels</InputLabel>
                  <Select
                    multiple
                    value={levels}
                    onChange={(e) => setLevels(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {['debug', 'info', 'warn', 'error'].map((level) => (
                      <MenuItem key={level} value={level}>
                        <Checkbox checked={levels.indexOf(level) > -1} />
                        <ListItemText primary={level} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Advanced Filters */}
        <Accordion expanded={expandedSections.advanced} onChange={() => toggleSection('advanced')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Advanced</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="User who triggered the operation"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Search Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Alert severity="info" sx={{ flexGrow: 1, mr: 2 }}>
            Type in search box above or select filters, then click "Apply Filters" to search.
          </Alert>
          <Button
            variant="outlined"
            onClick={clearAllFilters}
            disabled={activeFiltersCount === 0}
          >
            Clear Filters
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            size="large"
            sx={{
              minWidth: 150,
              fontWeight: 'bold'
            }}
          >
            Apply Filters
          </Button>
        </Box>

        {/* Active Filters Summary */}
        {activeFiltersCount > 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" icon={false}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" sx={{ mr: 1 }}>
                  Active filters:
                </Typography>
                {searchTerm && <Chip size="small" label={`Search: ${searchTerm}`} onDelete={() => setSearchTerm('')} />}
                {subsystems.length > 0 && <Chip size="small" label={`${subsystems.length} subsystems`} onDelete={() => setSubsystems([])} />}
                {operations.length > 0 && <Chip size="small" label={`${operations.length} operations`} onDelete={() => setOperations([])} />}
                {statuses.length > 0 && <Chip size="small" label={`${statuses.length} statuses`} onDelete={() => setStatuses([])} />}
                {(startDate || endDate) && <Chip size="small" label="Date range" onDelete={() => { setStartDate(null); setEndDate(null); }} />}
                {fileName && <Chip size="small" label={`File: ${fileName}`} onDelete={() => setFileName('')} />}
                {documentId && <Chip size="small" label={`Doc: ${documentId}`} onDelete={() => setDocumentId('')} />}
                {fileExtensions.length > 0 && <Chip size="small" label={`${fileExtensions.length} extensions`} onDelete={() => setFileExtensions([])} />}
                {artifactTypes.length > 0 && <Chip size="small" label={`${artifactTypes.length} artifact types`} onDelete={() => setArtifactTypes([])} />}
                {hasErrors && <Chip size="small" label="Errors only" onDelete={() => setHasErrors(false)} />}
                {hasTokens && <Chip size="small" label="Has tokens" onDelete={() => setHasTokens(false)} />}
              </Box>
            </Alert>
          </Box>
        )}
      </Paper>
    </LocalizationProvider>
  );
}

export default ProcessVisibilityAdvancedFilters;